import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import type { InboxMessageLight } from '@releve/shared';
import { supabase } from '@/integrations/supabase/client';
import { api, unwrap } from '@/lib/api';
import { addBreadcrumb, captureException, captureMessage } from '@/lib/sentry';
import type { RealtimeChannel } from '@supabase/supabase-js';

export type { InboxMessageLight } from '@releve/shared';

/**
 * The inbox list cache key — a bare `InboxMessageLight[]` the realtime handler
 * below and the optimistic writers (`useUpdateInboxMessage`) splice into.
 */
export const INBOX_MESSAGES_KEY = ['inbox-messages'] as const;

/**
 * One email in the list shape (`GET /api/v1/inbox/:id` — the same select as
 * the list, so a realtime INSERT spliced into the cache cannot drift from a
 * listed row). Null on any failure: a missed insert is picked up by the next
 * refetch.
 */
async function fetchSingleMessage(messageId: string): Promise<InboxMessageLight | null> {
  try {
    const res = await api.api.v1.inbox[':id'].$get({ param: { id: messageId } });
    return await unwrap<InboxMessageLight>(res);
  } catch (error) {
    captureMessage(
      `Error fetching inbox message: ${error instanceof Error ? error.message : String(error)}`,
      'error',
    );
    return null;
  }
}

/**
 * The caller's whole inbox, newest first (`GET /api/v1/inbox` — user-scoped,
 * keyset-paged on the server, never truncated). Filtering is client-side
 * (`InboxIndexGrid`). Realtime is handled by `useInboxUnreadCount` (always
 * mounted in the nav).
 */
export const useInboxMessages = () => {
  return useQuery({
    queryKey: INBOX_MESSAGES_KEY,
    queryFn: async () => {
      const res = await api.api.v1.inbox.$get();
      return unwrap<InboxMessageLight[]>(res);
    },
  });
};

// Fields that can be safely updated from realtime payload
const SAFE_UPDATE_FIELDS: (keyof InboxMessageLight)[] = [
  'status',
  'pdf_status',
  'pdf_file_id',
  'attachment_status',
  'message_type',
  'metadata',
  'linked_invitation_id',
  'linked_expense_id',
  'linked_ap_invoice_id',
];

// Helper hook for unread count - also sets up realtime subscription for all inbox queries
export const useInboxUnreadCount = () => {
  const queryClient = useQueryClient();
  const channelRef = useRef<RealtimeChannel | null>(null);

  // Real-time subscription - this is the single source of truth for inbox realtime updates
  // Since useInboxUnreadCount is always mounted in the navigation, this subscription is always active
  useEffect(() => {
    // If we already have a channel, don't create another
    if (channelRef.current) {
      return;
    }

    const channelName = 'inbox-changes';

    // Check for existing channel (handles React Strict Mode double-mount)
    const existingChannel = supabase.getChannels().find(ch => ch.topic === `realtime:${channelName}`);
    if (existingChannel) {
      return;
    }

    const channel = supabase
      .channel(channelName)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'inbox_messages'
      }, async (payload) => {
        if (payload.eventType === 'INSERT') {
          // Fetch the new message with joined data and add to cache
          const newMessage = await fetchSingleMessage(payload.new.id);
          if (newMessage) {
            queryClient.setQueriesData<InboxMessageLight[]>(
              { queryKey: ['inbox-messages'] },
              (oldData) => {
                if (!oldData) return undefined;
                if (oldData.some(m => m.id === newMessage.id)) return oldData;
                return [newMessage, ...oldData];
              }
            );
            if (newMessage.status === 'unread') {
              queryClient.setQueryData<number>(
                ['inbox-unread-count'],
                (old) => (old ?? 0) + 1
              );
            }
          }
        } else if (payload.eventType === 'UPDATE') {
          const updatedRecord = payload.new as Record<string, unknown>;
          const oldRecord = payload.old as Record<string, unknown>;

          queryClient.setQueriesData<InboxMessageLight[]>(
            { queryKey: ['inbox-messages'] },
            (oldData) => {
              if (!oldData) return undefined;
              return oldData.map(msg => {
                if (msg.id === updatedRecord.id) {
                  // Only spread known safe fields to prevent cache corruption
                  const safeUpdates: Partial<InboxMessageLight> = {};
                  for (const field of SAFE_UPDATE_FIELDS) {
                    if (field in updatedRecord) {
                      (safeUpdates as Record<string, unknown>)[field] = updatedRecord[field];
                    }
                  }
                  return { ...msg, ...safeUpdates };
                }
                return msg;
              });
            }
          );

          if (oldRecord.status !== updatedRecord.status) {
            if (updatedRecord.status === 'unread') {
              queryClient.setQueryData<number>(['inbox-unread-count'], (old) => (old ?? 0) + 1);
            } else if (oldRecord.status === 'unread') {
              queryClient.setQueryData<number>(['inbox-unread-count'], (old) => Math.max((old ?? 1) - 1, 0));
            }
          }

          void queryClient.invalidateQueries({ queryKey: ['inbox-message-detail', updatedRecord.id] });
        } else if (payload.eventType === 'DELETE') {
          const deletedRecord = payload.old as Record<string, unknown>;

          queryClient.setQueriesData<InboxMessageLight[]>(
            { queryKey: ['inbox-messages'] },
            (oldData) => {
              if (!oldData) return undefined;
              return oldData.filter(msg => msg.id !== deletedRecord.id);
            }
          );

          if (deletedRecord.status === 'unread') {
            queryClient.setQueryData<number>(['inbox-unread-count'], (old) => Math.max((old ?? 1) - 1, 0));
          }

          void queryClient.invalidateQueries({ queryKey: ['inbox-message-detail', deletedRecord.id] });
        }
      })
      .subscribe((status, err) => {
        if (!err) return;
        // Transient transport drops (network blip, laptop sleep, proxy idle
        // timeout) arrive as these normalized messages and realtime
        // auto-reconnects, so they are breadcrumbs, not Sentry events. Server
        // join rejections and binding mismatches still get reported.
        if (/^(socket closed: \d+|heartbeat timeout|channel error: (transport failure|connection lost))/.test(err.message)) {
          addBreadcrumb(err.message, { status }, 'warning');
          return;
        }
        captureException(err instanceof Error ? err : new Error(String(err)), {
          context: 'Inbox Realtime Subscription',
          status,
        });
      });

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        void supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [queryClient]);

  return useQuery({
    queryKey: ['inbox-unread-count'],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('inbox_messages')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'unread');

      if (error) throw error;
      return count || 0;
    }
  });
};
